import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Search, FileText, Folder, Upload, X, Clock, Pin, Users, BookOpen, CreditCard, BarChart2, GraduationCap, Building2, UserCheck, ChevronRight, Sparkles, Layers, Shield, Copy, FolderTree } from "lucide-react";

interface CommandItem {
  id: string;
  label: string;
  category: string;
  route: string;
  icon: React.ReactNode;
  keywords: string[];
  roles?: string[];
  permission?: string;
}

const ALL_COMMANDS: CommandItem[] = [
  { id: "documents-knowledge", label: "Knowledge Base", category: "Knowledge", route: "/documents/knowledge", icon: <BookOpen className="w-4 h-4 text-emerald-400" />, keywords: ["knowledge", "notes", "kb", "article", "policy"] },
  { id: "documents-approvals", label: "Approval Center", category: "Documents", route: "/documents/approvals", icon: <Shield className="w-4 h-4 text-amber-400" />, keywords: ["approval", "review", "workflow"], roles: ["super_admin", "administrator", "principal", "dean", "hod"] },
  { id: "documents-ocr", label: "OCR Text Extraction", category: "Documents", route: "/documents/ocr", icon: <Sparkles className="w-4 h-4 text-cyan-400" />, keywords: ["ocr", "text", "scan", "read"] },
  { id: "documents-storage", label: "Storage Analytics", category: "Documents", route: "/documents/storage", icon: <Layers className="w-4 h-4 text-purple-400" />, keywords: ["storage", "quota", "bytes", "usage"], roles: ["super_admin", "administrator", "it_support"] },
  { id: "documents-templates", label: "Template Library", category: "Documents", route: "/documents/templates", icon: <Copy className="w-4 h-4 text-teal-400" />, keywords: ["template", "forms", "blueprint"] },
  { id: "documents-dashboard", label: "Document Center Dashboard", category: "Documents", route: "/documents", icon: <FileText className="w-4 h-4 text-indigo-400" />, keywords: ["documents", "files", "repository", "docs"] },
  { id: "documents-explorer", label: "Document Explorer", category: "Documents", route: "/documents/explorer", icon: <Folder className="w-4 h-4 text-emerald-400" />, keywords: ["explorer", "folders", "files", "directories"] },
  { id: "documents-upload", label: "Upload Document", category: "Actions", route: "/documents/upload", icon: <Upload className="w-4 h-4 text-amber-400" />, keywords: ["upload", "add file", "attach"] },
  { id: "students", label: "Student Directory", category: "Pages", route: "/students", icon: <Users className="w-4 h-4 text-indigo-400" />, keywords: ["students", "enrollment", "directory"], roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "admission_officer"] },
  { id: "students-create", label: "Enroll New Student", category: "Actions", route: "/students/create", icon: <Users className="w-4 h-4 text-emerald-400" />, keywords: ["enroll", "add student", "register"], roles: ["super_admin", "administrator", "principal", "admission_officer"] },
  { id: "staff", label: "Faculty & Staff", category: "Pages", route: "/staff", icon: <UserCheck className="w-4 h-4 text-indigo-400" />, keywords: ["faculty", "staff", "teacher"], roles: ["super_admin", "administrator", "principal", "dean", "hod", "hr_manager"] },
  { id: "departments", label: "Academic Departments", category: "Academics", route: "/academics/departments", icon: <Building2 className="w-4 h-4 text-purple-400" />, keywords: ["departments", "academics"] },
  { id: "timetable", label: "Class Timetables", category: "Academics", route: "/timetable", icon: <BookOpen className="w-4 h-4 text-indigo-400" />, keywords: ["timetable", "schedule", "classes"] },
  { id: "results", label: "Grades & Results", category: "Academics", route: "/results", icon: <GraduationCap className="w-4 h-4 text-amber-400" />, keywords: ["results", "grades", "marks"] },
  { id: "fees", label: "Fee Structures", category: "Finance", route: "/fees", icon: <CreditCard className="w-4 h-4 text-emerald-400" />, keywords: ["fees", "finance", "tuition"], roles: ["super_admin", "administrator", "principal", "accountant", "student", "parent"] },
  { id: "payments", label: "Fee Collections", category: "Finance", route: "/payments", icon: <CreditCard className="w-4 h-4 text-emerald-400" />, keywords: ["payments", "collections", "receipts"], roles: ["super_admin", "administrator", "accountant", "student", "parent"] },
  { id: "library", label: "Digital Library", category: "Campus", route: "/library", icon: <BookOpen className="w-4 h-4 text-amber-400" />, keywords: ["library", "books", "catalog"], roles: ["super_admin", "administrator", "principal", "librarian", "faculty", "student"] },
  { id: "hostel", label: "Hostel & Beds", category: "Campus", route: "/hostel", icon: <Building2 className="w-4 h-4 text-purple-400" />, keywords: ["hostel", "beds", "rooms"], roles: ["super_admin", "administrator", "hostel_warden", "student", "parent"] },
  { id: "ai-university-copilot", label: "Master University AI Copilot Console", category: "AI Platform", route: "/ai/university-copilot", icon: <Sparkles className="w-4 h-4 text-blue-400" />, keywords: ["master", "university", "copilot", "erp ai", "global"], roles: ["super_admin", "administrator", "principal"] },
  { id: "ai-copilot-global-chat", label: "Global Role-Aware AI Chat", category: "AI Platform", route: "/ai/copilot/global-chat", icon: <Sparkles className="w-4 h-4 text-blue-400" />, keywords: ["chat", "global", "qa", "assistant"] },
  { id: "lms-dashboard", label: "Enterprise LMS Foundation Console", category: "LMS Platform", route: "/lms/dashboard", icon: <BookOpen className="w-4 h-4 text-emerald-400" />, keywords: ["lms", "learning", "courses", "dashboard"] },
  { id: "lms-catalog", label: "University Course Catalog", category: "LMS Platform", route: "/lms/catalog", icon: <BookOpen className="w-4 h-4 text-emerald-400" />, keywords: ["catalog", "courses", "enroll", "degree"] },
  { id: "lms-my-courses", label: "My Enrolled Courses Workspace", category: "LMS Platform", route: "/lms/my-courses", icon: <BookOpen className="w-4 h-4 text-teal-400" />, keywords: ["my courses", "enrolled", "workspace", "learning"] },
  { id: "lms-builder", label: "Course Structure Hierarchy Builder", category: "LMS Platform", route: "/lms/builder", icon: <Layers className="w-4 h-4 text-purple-400" />, keywords: ["builder", "modules", "chapters", "lessons"], roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty"] },
  { id: "lms-management-approvals", label: "Course Approval Queue Center", category: "LMS Platform", route: "/lms/management/approvals", icon: <Shield className="w-4 h-4 text-amber-400" />, keywords: ["approvals", "review", "approve course", "publish"], roles: ["super_admin", "administrator", "principal", "dean", "hod"] },
  { id: "lms-enhancements-blueprints", label: "Academic Course Blueprint Library", category: "LMS Platform", route: "/lms/enhancements/blueprints", icon: <Layers className="w-4 h-4 text-teal-400" />, keywords: ["blueprint", "curriculum blueprint", "accreditation"], roles: ["super_admin", "administrator", "principal", "dean", "hod"] },
  { id: "lms-enhancements-curriculum", label: "Academic Curriculum Designer", category: "LMS Platform", route: "/lms/enhancements/curriculum", icon: <FolderTree className="w-4 h-4 text-purple-400" />, keywords: ["curriculum", "program structure", "semester"], roles: ["super_admin", "administrator", "principal", "dean", "hod"] },
  { id: "lms-enhancements-workload", label: "Faculty Workload Engine Dashboard", category: "LMS Platform", route: "/lms/enhancements/workload", icon: <Clock className="w-4 h-4 text-amber-400" />, keywords: ["faculty workload", "teaching hours", "overload", "credits"], roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty"] },
  { id: "ai-faculty-copilot", label: "Enterprise Faculty AI Copilot Console", category: "AI Platform", route: "/ai/faculty-copilot", icon: <Sparkles className="w-4 h-4 text-indigo-400" />, keywords: ["faculty", "copilot", "lesson plan", "academic"], roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty"] },
  { id: "dashboard-super-admin", label: "Super Admin Dashboard", category: "Dashboards", route: "/dashboard/super-admin", icon: <BarChart2 className="w-4 h-4 text-indigo-400" />, keywords: ["dashboard", "super admin"], roles: ["super_admin", "administrator"] },
  { id: "dashboard-principal", label: "Principal Dashboard", category: "Dashboards", route: "/dashboard/principal", icon: <BarChart2 className="w-4 h-4 text-indigo-400" />, keywords: ["dashboard", "principal"], roles: ["super_admin", "administrator", "principal", "dean"] },
];

const PINNED_SHORTCUTS = [
  { label: "Enroll Student", route: "/students/create", icon: <Users className="w-3.5 h-3.5 text-emerald-400" /> },
  { label: "Collect Fee", route: "/payments/collect", icon: <CreditCard className="w-3.5 h-3.5 text-indigo-400" /> },
  { label: "View Reports", route: "/reports/naac-nirf", icon: <BarChart2 className="w-3.5 h-3.5 text-purple-400" /> },
];

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [recentSearches, setRecentSearches] = useState<string[]>(["students", "fee collection", "timetable"]);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user, hasRole, hasPermission } = useAuth();

  // Deduplicate and filter commands by user's RBAC roles & permissions
  const roleFilteredCommands = ALL_COMMANDS.filter((cmd, idx, self) => {
    // Unique check by route or id
    if (self.findIndex((c) => c.route === cmd.route) !== idx) return false;

    if (!user) return false;

    if (cmd.roles && cmd.roles.length > 0) {
      if (!hasRole(cmd.roles)) return false;
    }
    if (cmd.permission) {
      if (!hasPermission(cmd.permission)) return false;
    }

    return true;
  });

  const filtered = query.trim()
    ? roleFilteredCommands.filter(
        (cmd) =>
          cmd.label.toLowerCase().includes(query.toLowerCase()) ||
          cmd.keywords.some((k) => k.includes(query.toLowerCase()))
      )
    : roleFilteredCommands.slice(0, 6);

  const grouped = filtered.reduce<Record<string, CommandItem[]>>((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleSelect = useCallback(
    (cmd: CommandItem) => {
      setRecentSearches((prev) => [cmd.label, ...prev.filter((s) => s !== cmd.label)].slice(0, 5));
      onClose();
    },
    [onClose]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-start justify-center pt-20 px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ type: "spring", damping: 30, stiffness: 400 }}
            className="w-full max-w-xl bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
              <Search className="w-4 h-4 text-slate-400 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search pages, students, reports, actions..."
                className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 focus:outline-none"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-slate-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              )}
              <kbd className="text-[10px] font-mono text-slate-500 border border-slate-700 rounded px-1.5 py-0.5">ESC</kbd>
            </div>

            <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-800/60">
              {/* Pinned Shortcuts (when no query) */}
              {!query && (
                <div className="p-3 space-y-1">
                  <p className="text-[10px] font-mono font-bold text-slate-500 uppercase px-2 mb-2 flex items-center gap-1">
                    <Pin className="w-3 h-3" /> Pinned Shortcuts
                  </p>
                  {PINNED_SHORTCUTS.map((s, i) => (
                    <Link
                      key={i}
                      to={s.route}
                      onClick={onClose}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      {s.icon}
                      <span>{s.label}</span>
                      <ChevronRight className="w-3.5 h-3.5 ml-auto text-slate-600" />
                    </Link>
                  ))}
                </div>
              )}

              {/* Recent Searches (when no query) */}
              {!query && recentSearches.length > 0 && (
                <div className="p-3 space-y-1">
                  <p className="text-[10px] font-mono font-bold text-slate-500 uppercase px-2 mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Recent Searches
                  </p>
                  {recentSearches.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setQuery(s)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs text-slate-400 hover:bg-slate-800 hover:text-white text-left transition-colors"
                    >
                      <Clock className="w-3.5 h-3.5 text-slate-600" />
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Search Results */}
              {Object.entries(grouped).map(([category, items]) => (
                <div key={category} className="p-3 space-y-1">
                  <p className="text-[10px] font-mono font-bold text-slate-500 uppercase px-2 mb-2">{category}</p>
                  {items.map((cmd) => (
                    <Link
                      key={cmd.id}
                      to={cmd.route}
                      onClick={() => handleSelect(cmd)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      {cmd.icon}
                      <span className="flex-1">{cmd.label}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
                    </Link>
                  ))}
                </div>
              ))}

              {query && filtered.length === 0 && (
                <div className="p-8 text-center">
                  <p className="text-sm text-slate-400">No results for <strong className="text-white">"{query}"</strong></p>
                </div>
              )}
            </div>

            {/* Footer Hints */}
            <div className="px-4 py-2 border-t border-slate-800 flex items-center justify-between text-[10px] font-mono text-slate-600">
              <span>â†‘â†“ Navigate â€¢ Enter Select â€¢ ESC Close</span>
              <span>âŒ˜K Global Search</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

